import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function GDPRBanner() {
	const [isVisible, setIsVisible] = useState(false);
	const [showDetails, setShowDetails] = useState(false);

	const { t } = useTranslation();

	useEffect(() => {
		const consent = localStorage.getItem('gdpr-consent');
		if (!consent) {
			setIsVisible(true);
		} else {
			initializeGtag(JSON.parse(consent));
		}
	}, []);

	const initializeGtag = (consent: any) => {
		if (typeof window !== 'undefined' && window?.gtag) {
			window?.gtag('consent', 'update', {
				ad_storage: consent.marketing ? 'granted' : 'denied',
				ad_user_data: consent.marketing ? 'granted' : 'denied',
				ad_personalization: consent.marketing ? 'granted' : 'denied',
				analytics_storage: consent.analytics ? 'granted' : 'denied',
			});
		}
	};

	const handleAcceptAll = () => {
		const consent = {
			necessary: true,
			analytics: true,
			marketing: true,
		};
		localStorage.setItem('gdpr-consent', JSON.stringify(consent));
		initializeGtag(consent);
		setIsVisible(false);
	};

	const handleAcceptNecessary = () => {
		const consent = {
			necessary: true,
			analytics: false,
			marketing: false,
		};
		localStorage.setItem('gdpr-consent', JSON.stringify(consent));
		initializeGtag(consent);
		setIsVisible(false);
	};

	const handleCustomSave = () => {
		const analytics = document?.getElementById('analytics-checkbox') as any;
		const marketing = document?.getElementById('marketing-checkbox') as any;

		const consent = {
			necessary: true,
			analytics: analytics?.checked,
			marketing: marketing?.checked,
		};
		localStorage.setItem('gdpr-consent', JSON.stringify(consent));
		initializeGtag(consent);
		setIsVisible(false);
	};

	if (!isVisible) return null;
	if (import.meta.env.MODE === 'development') return;

	return (
		<div className="fixed bottom-4 right-4 z-50 max-w-md w-full mx-4 max-sm:mx-0 max-sm:right-0 max-sm:bottom-0">
			<div className="bg-white rounded-lg shadow-2xl border border-gray-200 max-h-[85vh] overflow-y-auto">
				<div className="bg-[var(--primary)] text-white p-6 rounded-t-lg">
					<div className="flex items-center gap-3">
						<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
							/>
						</svg>
						<h2 className="text-2xl font-bold">{t('cookies.title')}</h2>
					</div>
				</div>

				<div className="p-4 pb-0">
					<p className="text-gray-700 mb-4">{t('cookies.description')}</p>

					{!showDetails ? (
						<button
							onClick={() => setShowDetails(true)}
							className="text-[var(--primary)] font-medium underline cursor-pointer"
						>
							{t('cookies.settings')}
						</button>
					) : (
						<div className="space-y-4 mb-4 bg-gray-50 p-4 rounded-lg">
							<div className="flex items-start gap-3">
								<input
									type="checkbox"
									id="necessary-checkbox"
									checked
									disabled
									className="mt-1 w-5 h-5 accent-[var(--primary)]"
								/>
								<div className="flex-1">
									<label htmlFor="necessary-checkbox" className="font-semibold text-gray-900 block">
										{t('cookies.necessary_title')}
									</label>
									<p className="text-sm text-gray-600">{t('cookies.necessary_description')}</p>
								</div>
							</div>

							<div className="flex items-start gap-3">
								<input
									type="checkbox"
									id="analytics-checkbox"
									defaultChecked
									className="mt-1 w-5 h-5 accent-[var(--primary)] cursor-pointer"
								/>
								<div className="flex-1">
									<label
										htmlFor="analytics-checkbox"
										className="font-semibold text-gray-900 block cursor-pointer"
									>
										{t('cookies.analytics_title')}
									</label>
									<p className="text-sm text-gray-600">{t('cookies.analytics_description')}</p>
								</div>
							</div>

							<div className="flex items-start gap-3">
								<input
									type="checkbox"
									id="marketing-checkbox"
									defaultChecked
									className="mt-1 w-5 h-5 accent-[var(--primary)] cursor-pointer"
								/>
								<div className="flex-1">
									<label
										htmlFor="marketing-checkbox"
										className="font-semibold text-gray-900 block cursor-pointer"
									>
										{t('cookies.marketing_title')}
									</label>
									<p className="text-sm text-gray-600">{t('cookies.marketing_description')}</p>
								</div>
							</div>
						</div>
					)}

					<div className="flex flex-col sm:flex-row py-4 gap-3 sticky bottom-0 bg-white">
						{showDetails ? (
							<>
								<button
									onClick={handleCustomSave}
									className="flex-1 bg-[var(--primary)] hover:bg-[var(--primary)]/80 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 cursor-pointer"
								>
									{t('cookies.save_settings')}
								</button>
								<button
									onClick={handleAcceptNecessary}
									className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200 cursor-pointer"
								>
									{t('cookies.only_necessary')}
								</button>
							</>
						) : (
							<>
								<button
									onClick={handleAcceptAll}
									className="flex-1 bg-[var(--primary)] text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 shadow-lg cursor-pointer"
								>
									{t('cookies.accept_all')}
								</button>
								<button
									onClick={handleAcceptNecessary}
									className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors duration-200 cursor-pointer"
								>
									{t('cookies.only_necessary')}
								</button>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
